import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Driver appearance designer — used for the boxing boss fight portrait.
 * Player picks from preset skin/hair/shirt color palettes + a hair style.
 * Lightweight on purpose: a full pixel-art designer is overkill for a
 * boxing-fight portrait, and presets keep the storage tiny.
 *
 * Renders a small pixel-art-ish character preview alongside the picks.
 */
export default function DriverDesigner({ value, onChange }) {
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/driver`, { withCredentials: true })
      .then((r) => {
        setOptions(r.data.options);
        if (!value && onChange) onChange(r.data.driver);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load driver options"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const update = useCallback((patch) => {
    onChange?.({ ...value, ...patch });
  }, [value, onChange]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${BACKEND_URL}/api/driver`, value, { withCredentials: true });
      toast.success("Driver saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (!options || !value) return null;

  return (
    <div className="grid sm:grid-cols-[180px_1fr] gap-6" data-testid="driver-designer">
      <DriverPortrait driver={value} size={160} />
      <div className="space-y-3">
        <Picker
          label="Skin"
          value={value.skin}
          choices={options.skin}
          onChange={(v) => update({ skin: v })}
          testid="driver-skin"
        />
        <Picker
          label="Hair color"
          value={value.hair}
          choices={options.hair}
          onChange={(v) => update({ hair: v })}
          testid="driver-hair"
        />
        <StyleStrip
          label="Hair style"
          value={value.hair_style}
          choices={options.hair_style}
          onChange={(v) => update({ hair_style: v })}
        />
        <Picker
          label="Shirt"
          value={value.shirt}
          choices={options.shirt}
          onChange={(v) => update({ shirt: v })}
          testid="driver-shirt"
        />
        <button
          onClick={save}
          disabled={busy}
          data-testid="driver-save"
          className="w-full mt-2 px-4 py-2 bg-[#FFCC00] text-black font-heading uppercase text-sm tracking-wider disabled:opacity-40"
        >
          {busy ? "Saving…" : "Save driver"}
        </button>
      </div>
    </div>
  );
}

function Picker({ label, value, choices, onChange, testid }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.2em] uppercase text-white/50 mb-1">{label}</div>
      <div className="flex flex-wrap gap-2" data-testid={testid}>
        {choices.map((c) => (
          <button
            key={c}
            onClick={() => onChange(c)}
            data-testid={`${testid}-${c.replace("#", "").toLowerCase()}`}
            className={`w-7 h-7 border-2 transition-transform ${value === c ? "border-[#FFCC00] scale-110" : "border-white/20 hover:border-white/50"}`}
            style={{ background: c }}
            aria-label={c}
          />
        ))}
      </div>
    </div>
  );
}

function StyleStrip({ label, value, choices, onChange }) {
  return (
    <div>
      <div className="text-[11px] tracking-[0.2em] uppercase text-white/50 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5" data-testid="driver-hair-style">
        {choices.map((s) => (
          <button
            key={s}
            onClick={() => onChange(s)}
            data-testid={`driver-hair-style-${s}`}
            className={`px-3 py-1 border text-[11px] tracking-[0.2em] uppercase transition-colors ${value === s ? "border-[#FFCC00] text-[#FFCC00] bg-[#FFCC00]/10" : "border-white/20 text-white/60 hover:text-white"}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Render a chunky pixel-art character portrait from the driver dict.
 * Drawn on a tiny grid then scaled up so it reads as retro 8-bit.
 */
export function DriverPortrait({ driver, size = 64, dataTestId }) {
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    if (!driver) return;
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    cvs.width = size * dpr; cvs.height = size * dpr;
    cvs.style.width = `${size}px`; cvs.style.height = `${size}px`;
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = false;

    // 16×16 pixel grid scaled up. Centered character.
    const G = 16; // grid size
    const px = size / G;
    const skin = driver.skin || "#E5BD9A";
    const hair = driver.hair || "#1A1A1A";
    const shirt = driver.shirt || "#FF3B30";
    const style = driver.hair_style || "short";

    // Background — stadium ring lighting
    const grad = ctx.createRadialGradient(size / 2, size * 0.4, 4, size / 2, size / 2, size / 1.4);
    grad.addColorStop(0, "#1F1F1F");
    grad.addColorStop(1, "#0A0A0A");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);

    // Helper to paint a grid cell
    const cell = (gx, gy, color) => {
      ctx.fillStyle = color;
      ctx.fillRect(gx * px, gy * px, px, px);
    };
    const block = (x0, y0, w, h, color) => {
      for (let gx = x0; gx < x0 + w; gx++) {
        for (let gy = y0; gy < y0 + h; gy++) cell(gx, gy, color);
      }
    };

    // Head + face (centered around col 6-9, rows 3-6)
    block(6, 3, 4, 4, skin);
    // Eyes
    cell(6, 4, "#1A1A1A"); cell(9, 4, "#1A1A1A");
    // Mouth
    block(7, 5, 2, 1, "#3B2A22");

    // Hair styles
    if (style === "buzz") {
      block(6, 2, 4, 1, hair);
    } else if (style === "short") {
      block(6, 2, 4, 1, hair);
      cell(5, 3, hair); cell(10, 3, hair);
    } else if (style === "long") {
      block(5, 2, 6, 1, hair);
      cell(5, 3, hair); cell(10, 3, hair);
      cell(5, 4, hair); cell(10, 4, hair);
      cell(5, 5, hair); cell(10, 5, hair);
    } else if (style === "mohawk") {
      block(7, 1, 2, 3, hair);
    } else if (style === "cap") {
      block(5, 2, 6, 1, hair);
      block(6, 3, 4, 1, hair);
      block(11, 3, 1, 1, hair); // brim
    }

    // Neck
    block(7, 7, 2, 1, skin);
    // Shoulders / shirt
    block(4, 8, 8, 4, shirt);
    // Arms (gloves for the boxing match)
    block(3, 9, 1, 2, "#C8102E");
    block(12, 9, 1, 2, "#C8102E");
    // Belt
    block(4, 12, 8, 1, "#1A1A1A");
  }, [driver, size]);

  return <canvas ref={canvasRef} data-testid={dataTestId || "driver-portrait"} className="border border-white/10" />;
}
